// src/scripts/training/allergenTrainingGate.js

import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  serverTimestamp,
  setDoc,
  Timestamp,
  updateDoc,
  where,
} from "firebase/firestore";

import { auth, db } from "@/firebase";

export const ALLERGEN_TRAINING_COURSE_ID = "allergen_awareness_uk_2026";
export const ALLERGEN_TRAINING_TITLE = "Allergen Awareness Training";
export const ALLERGEN_TRAINING_REQUIRED_MONTHS = 12;
export const ALLERGEN_TRAINING_GRACE_DAYS = 0;
export const ALLERGEN_TRAINING_PASS_MARK = 85;

function assertFirebaseReady() {
  if (!db) {
    throw new Error(
      "Allergen training Firebase database has not been configured. Check that @/firebase exports db."
    );
  }

  return { db, auth };
}

function clean(value) {
  return String(value ?? "").trim();
}

function lower(value) {
  return clean(value).toLowerCase();
}

function toDate(value) {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value.toDate === "function") return value.toDate();

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function addMonths(date, months) {
  const copy = new Date(date);
  copy.setMonth(copy.getMonth() + Number(months || 0));
  return copy;
}

function addDays(date, days) {
  const copy = new Date(date);
  copy.setDate(copy.getDate() + Number(days || 0));
  return copy;
}

function safeTimestampFromDate(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
  return Timestamp.fromDate(date);
}

function firstNonEmpty(...values) {
  for (const value of values) {
    const cleaned = clean(value);
    if (cleaned) return cleaned;
  }

  return "";
}

export function normaliseStaffId(value) {
  return clean(value);
}

function getStaffId(staff = {}) {
  if (!staff || typeof staff !== "object") return "";

  return firstNonEmpty(
    staff.staffId,
    staff.staff_id,
    staff.id,
    staff.uid,
    staff.employeeId,
    staff.employee_id,
    staff.docId,
    staff.documentId,
    staff.userId,
    staff.user_id,
    staff.firebaseUid,
    staff.firebase_uid,
    staff.staffDocumentId,
    staff.staff_document_id
  );
}

function getStaffName(staff = {}) {
  if (!staff || typeof staff !== "object") return "";

  const firstLast = `${clean(staff.firstName || staff.first_name)} ${clean(
    staff.lastName || staff.last_name
  )}`.trim();

  return firstNonEmpty(
    staff.staffName,
    staff.staff_name,
    staff.name,
    staff.fullName,
    staff.full_name,
    staff.displayName,
    staff.display_name,
    firstLast,
    staff.email,
    staff.staffEmail,
    staff.staff_email
  );
}

function getStaffEmail(staff = {}) {
  if (!staff || typeof staff !== "object") return "";

  return firstNonEmpty(
    staff.staffEmail,
    staff.staff_email,
    staff.email,
    staff.emailAddress,
    staff.email_address
  );
}

function isRealStaffObject(staff = {}) {
  if (!staff || typeof staff !== "object") return false;

  const staffId = getStaffId(staff);
  if (!staffId) return false;

  if (staff.isPlaceholder === true) return false;
  if (staff.placeholder === true) return false;
  if (staff.isHoliday === true) return false;
  if (staff.holidayOnly === true) return false;
  if (staff.isDeleted === true) return false;
  if (staff.deleted === true) return false;
  if (staff.archived === true) return false;

  return true;
}

export function getAllergenTrainingRecordId(
  staffId,
  courseId = ALLERGEN_TRAINING_COURSE_ID
) {
  const cleanStaffId = normaliseStaffId(staffId);

  if (!cleanStaffId) {
    throw new Error("Cannot build allergen training record id without staffId.");
  }

  return `${cleanStaffId}_${courseId}`;
}

export function getAllergenTrainingRecordRef(
  venueId,
  staffId,
  courseId = ALLERGEN_TRAINING_COURSE_ID
) {
  const { db: firestoreDb } = assertFirebaseReady();
  const cleanVenueId = clean(venueId);

  if (!cleanVenueId) {
    throw new Error("Cannot build allergen training record ref without venueId.");
  }

  return doc(
    firestoreDb,
    "venues",
    cleanVenueId,
    "training_records",
    getAllergenTrainingRecordId(staffId, courseId)
  );
}

function getTrainingRecordsCollectionRef(venueId) {
  const { db: firestoreDb } = assertFirebaseReady();
  const cleanVenueId = clean(venueId);

  if (!cleanVenueId) {
    throw new Error(
      "Cannot build allergen training records collection without venueId."
    );
  }

  return collection(firestoreDb, "venues", cleanVenueId, "training_records");
}

export function buildInitialAllergenTrainingRecord({
  venueId = "",
  staffId = "",
  staffName = "",
  staffEmail = "",
  courseId = ALLERGEN_TRAINING_COURSE_ID,
} = {}) {
  const cleanStaffId = normaliseStaffId(staffId);
  const cleanStaffEmail = clean(staffEmail);
  const recordId = getAllergenTrainingRecordId(cleanStaffId, courseId);

  return {
    id: recordId,
    recordId,

    venueId: clean(venueId),

    staffId: cleanStaffId,
    staffName: clean(staffName),
    staffEmail: cleanStaffEmail,
    staffEmailLower: lower(cleanStaffEmail),

    courseId,
    courseTitle: ALLERGEN_TRAINING_TITLE,
    courseVersion: "2026.1",
    trainingType: "allergen_awareness",

    required: true,
    status: "not_started",
    completed: false,
    passed: false,
    scorePercent: null,
    passMark: ALLERGEN_TRAINING_PASS_MARK,

    startedAt: null,
    completedAt: null,
    expiresAt: null,
    renewedAt: null,

    declarationAccepted: false,
    declarationAcceptedAt: null,

    sourceBasis: "CoreDeck internal allergen awareness training",

    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
}

async function findTrainingRecordByStaffIdAndCourseId({
  venueId,
  staffId,
  courseId = ALLERGEN_TRAINING_COURSE_ID,
}) {
  const cleanStaffId = normaliseStaffId(staffId);
  if (!cleanStaffId) return null;

  const recordsRef = getTrainingRecordsCollectionRef(venueId);

  const q = query(
    recordsRef,
    where("staffId", "==", cleanStaffId),
    where("courseId", "==", courseId),
    limit(1)
  );

  const snapshot = await getDocs(q);
  if (snapshot.empty) return null;

  const found = snapshot.docs[0];

  return {
    id: found.id,
    ref: found.ref,
    data: found.data() || {},
  };
}

async function findTrainingRecordByEmailAndCourseId({
  venueId,
  staffEmail,
  courseId = ALLERGEN_TRAINING_COURSE_ID,
}) {
  const emailLower = lower(staffEmail);
  if (!emailLower) return null;

  const recordsRef = getTrainingRecordsCollectionRef(venueId);

  const q = query(
    recordsRef,
    where("staffEmailLower", "==", emailLower),
    where("courseId", "==", courseId),
    limit(1)
  );

  const snapshot = await getDocs(q);
  if (snapshot.empty) return null;

  const found = snapshot.docs[0];

  return {
    id: found.id,
    ref: found.ref,
    data: found.data() || {},
  };
}

async function loadTrainingRecordForStaff({
  venueId,
  staffId,
  staffEmail = "",
  courseId = ALLERGEN_TRAINING_COURSE_ID,
}) {
  const cleanStaffId = normaliseStaffId(staffId);

  if (!clean(venueId) || !cleanStaffId) {
    return null;
  }

  const directRef = getAllergenTrainingRecordRef(venueId, cleanStaffId, courseId);
  const directSnapshot = await getDoc(directRef);

  if (directSnapshot.exists()) {
    return {
      id: directSnapshot.id,
      ref: directSnapshot.ref,
      data: directSnapshot.data() || {},
    };
  }

  const byStaffId = await findTrainingRecordByStaffIdAndCourseId({
    venueId,
    staffId: cleanStaffId,
    courseId,
  });

  if (byStaffId) return byStaffId;

  const byEmail = await findTrainingRecordByEmailAndCourseId({
    venueId,
    staffEmail,
    courseId,
  });

  if (byEmail) return byEmail;

  return null;
}

export async function ensureAllergenTrainingRecordForStaff({
  venueId,
  staffId,
  staffName = "",
  staffEmail = "",
  courseId = ALLERGEN_TRAINING_COURSE_ID,
} = {}) {
  assertFirebaseReady();

  const cleanVenueId = clean(venueId);
  const cleanStaffId = normaliseStaffId(staffId);

  if (!cleanVenueId) {
    throw new Error("Cannot ensure allergen training record without venueId.");
  }

  if (!cleanStaffId) {
    return {
      skipped: true,
      skippedReason: "missing_staff_id",
      id: "",
      recordId: "",
      staffId: "",
      staffName: clean(staffName),
      staffEmail: clean(staffEmail),
      courseId,
      courseTitle: ALLERGEN_TRAINING_TITLE,
      status: "skipped",
      completed: false,
      passed: false,
      valid: false,
      canWork: false,
      canBeAddedToRota: false,
      rotaBlocked: false,
    };
  }

  const existing = await loadTrainingRecordForStaff({
    venueId: cleanVenueId,
    staffId: cleanStaffId,
    staffEmail,
    courseId,
  });

  if (existing) {
    const existingData = existing.data || {};

    const patch = {
      courseId,
      courseTitle: existingData.courseTitle || ALLERGEN_TRAINING_TITLE,
      passMark: existingData.passMark || ALLERGEN_TRAINING_PASS_MARK,
      updatedAt: serverTimestamp(),
    };

    if (staffName && existingData.staffName !== clean(staffName)) {
      patch.staffName = clean(staffName);
    }

    if (staffEmail && existingData.staffEmail !== clean(staffEmail)) {
      patch.staffEmail = clean(staffEmail);
      patch.staffEmailLower = lower(staffEmail);
    }

    await updateDoc(existing.ref, patch);

    return {
      id: existing.id,
      recordId: existing.id,
      ...existingData,
      ...patch,
    };
  }

  const record = buildInitialAllergenTrainingRecord({
    venueId: cleanVenueId,
    staffId: cleanStaffId,
    staffName,
    staffEmail,
    courseId,
  });

  const recordRef = getAllergenTrainingRecordRef(
    cleanVenueId,
    cleanStaffId,
    courseId
  );

  await setDoc(recordRef, record, { merge: true });

  return {
    ...record,
    id: record.id,
    recordId: record.id,
  };
}

export function buildAllergenTrainingStatus(
  record,
  {
    staff = null,
    staffId = "",
    staffName = "",
    staffEmail = "",
    now = new Date(),
  } = {}
) {
  const source = record || {};

  const resolvedStaffId = firstNonEmpty(
    source.staffId,
    staffId,
    staff ? getStaffId(staff) : ""
  );

  const resolvedStaffName = firstNonEmpty(
    source.staffName,
    staffName,
    staff ? getStaffName(staff) : ""
  );

  const resolvedStaffEmail = firstNonEmpty(
    source.staffEmail,
    staffEmail,
    staff ? getStaffEmail(staff) : ""
  );

  if (!resolvedStaffId) {
    return {
      required: false,
      courseId: source.courseId || ALLERGEN_TRAINING_COURSE_ID,
      courseTitle: source.courseTitle || ALLERGEN_TRAINING_TITLE,

      staffId: "",
      staffName: resolvedStaffName,
      staffEmail: resolvedStaffEmail,

      exists: false,
      completed: false,
      passed: false,
      failed: false,
      expired: false,
      valid: false,

      status: "skipped",
      reason: "No staff id supplied.",
      message: "Skipped because this is not a valid staff rota row.",

      canWork: false,
      canBeAddedToRota: false,
      rotaBlocked: false,

      scorePercent: null,
      passMark: ALLERGEN_TRAINING_PASS_MARK,

      completedAt: null,
      expiresAt: null,

      record: record || null,
    };
  }

  const exists = !!record && source.skipped !== true;
  const completedAt = toDate(source.completedAt);
  const expiresAt = toDate(source.expiresAt);

  const scorePercent =
    source.scorePercent === null || source.scorePercent === undefined
      ? null
      : Number(source.scorePercent);

  const passMark = Number(source.passMark || ALLERGEN_TRAINING_PASS_MARK);

  const passed =
    source.passed === true ||
    source.status === "passed" ||
    source.status === "completed" ||
    (Number.isFinite(scorePercent) && scorePercent >= passMark);

  const completed =
    source.completed === true ||
    source.status === "completed" ||
    source.status === "passed" ||
    !!completedAt;

  const failed =
    source.status === "failed" ||
    (completed && Number.isFinite(scorePercent) && scorePercent < passMark);

  const graceExpiry = expiresAt
    ? addDays(expiresAt, ALLERGEN_TRAINING_GRACE_DAYS)
    : null;

  const expired = !!graceExpiry && graceExpiry.getTime() < now.getTime();
  const valid = exists && completed && passed && !expired;

  let status = "not_started";
  let reason = "No allergen training record found.";
  let message = "Allergen training has not been completed.";

  if (exists && valid) {
    status = "valid";
    reason = "";
    message = "Allergen training is complete and in date.";
  } else if (exists && expired) {
    status = "expired";
    reason = "Allergen training has expired.";
    message = "Allergen training has expired and must be renewed.";
  } else if (exists && failed) {
    status = "failed";
    reason = "Allergen training has not reached the required pass mark.";
    message = `Allergen training must be passed at ${passMark}% or above.`;
  } else if (exists && completed && !passed) {
    status = "failed";
    reason = "Allergen training was completed but not passed.";
    message = `Allergen training must be passed at ${passMark}% or above.`;
  } else if (exists && source.status === "in_progress") {
    status = "in_progress";
    reason = "Allergen training is in progress.";
    message = "Allergen training has been started but not completed.";
  }

  return {
    required: true,
    courseId: source.courseId || ALLERGEN_TRAINING_COURSE_ID,
    courseTitle: source.courseTitle || ALLERGEN_TRAINING_TITLE,

    staffId: resolvedStaffId,
    staffName: resolvedStaffName,
    staffEmail: resolvedStaffEmail,

    exists,
    completed,
    passed,
    failed,
    expired,
    valid,

    status,
    reason,
    message,

    canWork: valid,
    canBeAddedToRota: valid,
    rotaBlocked: !valid,

    scorePercent,
    passMark,

    completedAt,
    expiresAt,

    record: record || null,
  };
}

export function canStaffBeAddedToRota(statusOrRecord) {
  if (!statusOrRecord) return false;

  if (Object.prototype.hasOwnProperty.call(statusOrRecord, "canBeAddedToRota")) {
    return statusOrRecord.canBeAddedToRota === true;
  }

  return buildAllergenTrainingStatus(statusOrRecord).canBeAddedToRota === true;
}

export async function loadAllergenTrainingRecordsForVenue({
  venueId,
  staff = [],
  staffRows = [],
  staffMembers = [],
  employees = [],
  rows = [],
  teamMembers = [],
  courseId = ALLERGEN_TRAINING_COURSE_ID,
} = {}) {
  assertFirebaseReady();

  const cleanVenueId = clean(venueId);

  if (!cleanVenueId) {
    return {
      venueId: "",
      courseId,
      courseTitle: ALLERGEN_TRAINING_TITLE,

      recordsByStaffId: {},
      statusByStaffId: {},
      canAddByStaffId: {},

      blockedStaffIds: [],
      blockedStaff: [],
      validStaffIds: [],
      missingStaff: [],
      expiredStaff: [],
      failedStaff: [],

      totalStaffChecked: 0,
      totalBlocked: 0,
      totalValid: 0,

      loaded: false,
      error: "No venueId supplied.",
    };
  }

  const rawStaffList = [
    ...staff,
    ...staffRows,
    ...staffMembers,
    ...employees,
    ...rows,
    ...teamMembers,
  ].filter(Boolean);

  const uniqueStaffMap = new Map();

  for (const person of rawStaffList) {
    if (!isRealStaffObject(person)) continue;

    const staffId = getStaffId(person);

    if (!uniqueStaffMap.has(staffId)) {
      uniqueStaffMap.set(staffId, person);
    }
  }

  const uniqueStaff = Array.from(uniqueStaffMap.values());

  const recordsByStaffId = {};
  const statusByStaffId = {};
  const canAddByStaffId = {};
  const blockedStaff = [];
  const missingStaff = [];
  const expiredStaff = [];
  const failedStaff = [];
  const validStaffIds = [];

  await Promise.all(
    uniqueStaff.map(async (person) => {
      const staffId = getStaffId(person);
      const staffName = getStaffName(person);
      const staffEmail = getStaffEmail(person);

      if (!staffId) return;

      const found = await loadTrainingRecordForStaff({
        venueId: cleanVenueId,
        staffId,
        staffEmail,
        courseId,
      });

      const record = found
        ? {
            id: found.id,
            recordId: found.id,
            ...found.data,
          }
        : null;

      if (record) {
        recordsByStaffId[staffId] = record;
      }

      const status = buildAllergenTrainingStatus(record, {
        staff: person,
        staffId,
        staffName,
        staffEmail,
      });

      statusByStaffId[staffId] = status;
      canAddByStaffId[staffId] = status.canBeAddedToRota === true;

      if (status.valid) {
        validStaffIds.push(staffId);
        return;
      }

      const blockedItem = {
        staffId,
        staffName,
        staffEmail,
        status: status.status,
        reason: status.reason,
        message: status.message,
      };

      blockedStaff.push(blockedItem);

      if (!status.exists) {
        missingStaff.push(blockedItem);
      } else if (status.expired) {
        expiredStaff.push(blockedItem);
      } else if (status.failed) {
        failedStaff.push(blockedItem);
      }
    })
  );

  const blockedStaffIds = blockedStaff.map((item) => item.staffId);

  return {
    venueId: cleanVenueId,
    courseId,
    courseTitle: ALLERGEN_TRAINING_TITLE,

    recordsByStaffId,
    statusByStaffId,
    canAddByStaffId,

    blockedStaffIds,
    blockedStaff,
    validStaffIds,
    missingStaff,
    expiredStaff,
    failedStaff,

    totalStaffChecked: uniqueStaff.length,
    totalBlocked: blockedStaff.length,
    totalValid: validStaffIds.length,

    loaded: true,
    error: "",
    loadedAt: new Date().toISOString(),
  };
}

export async function loadAllergenTrainingGateForRota(options = {}) {
  const result = await loadAllergenTrainingRecordsForVenue(options);

  return {
    ...result,

    gateEnabled: true,
    gateLoaded: result.loaded === true,
    gateError: result.error || "",

    isStaffBlocked(staffId) {
      const cleanStaffId = normaliseStaffId(staffId);
      if (!cleanStaffId) return false;
      return result.blockedStaffIds.includes(cleanStaffId);
    },

    canStaffBeAdded(staffId) {
      const cleanStaffId = normaliseStaffId(staffId);
      if (!cleanStaffId) return false;
      return result.canAddByStaffId[cleanStaffId] === true;
    },

    getStatusForStaff(staffId) {
      const cleanStaffId = normaliseStaffId(staffId);
      if (!cleanStaffId) return null;
      return result.statusByStaffId[cleanStaffId] || null;
    },
  };
}

export async function completeAllergenTraining({
  venueId,
  staffId,
  staffName = "",
  staffEmail = "",
  scorePercent = 0,
  answers = [],
  questionResults = [],
  declarationAccepted = true,
  courseSnapshot = null,
  moduleSnapshot = null,
  quizSnapshot = null,
  courseId = ALLERGEN_TRAINING_COURSE_ID,
} = {}) {
  assertFirebaseReady();

  const cleanVenueId = clean(venueId);
  const cleanStaffId = normaliseStaffId(staffId);
  const score = Number(scorePercent || 0);
  const passed = score >= ALLERGEN_TRAINING_PASS_MARK;

  if (!cleanVenueId) {
    throw new Error("Cannot complete allergen training without venueId.");
  }

  if (!cleanStaffId) {
    throw new Error("Cannot complete allergen training without staffId.");
  }

  const now = new Date();
  const expiresAt = passed
    ? addMonths(now, ALLERGEN_TRAINING_REQUIRED_MONTHS)
    : null;

  const recordRef = getAllergenTrainingRecordRef(
    cleanVenueId,
    cleanStaffId,
    courseId
  );

  const recordId = getAllergenTrainingRecordId(cleanStaffId, courseId);

  const patch = {
    id: recordId,
    recordId,

    venueId: cleanVenueId,

    staffId: cleanStaffId,
    staffName: clean(staffName),
    staffEmail: clean(staffEmail),
    staffEmailLower: lower(staffEmail),

    courseId,
    courseTitle: ALLERGEN_TRAINING_TITLE,
    courseVersion: "2026.1",
    trainingType: "allergen_awareness",

    required: true,
    status: passed ? "completed" : "failed",
    completed: true,
    passed,
    scorePercent: score,
    passMark: ALLERGEN_TRAINING_PASS_MARK,

    completedAt: Timestamp.fromDate(now),
    expiresAt: safeTimestampFromDate(expiresAt),

    declarationAccepted: declarationAccepted === true,
    declarationAcceptedAt:
      declarationAccepted === true ? Timestamp.fromDate(now) : null,

    answers,
    questionResults,
    courseSnapshot,
    moduleSnapshot,
    quizSnapshot,

    sourceBasis: "CoreDeck internal allergen awareness training",
    updatedAt: serverTimestamp(),
  };

  const existing = await getDoc(recordRef);

  if (existing.exists()) {
    await updateDoc(recordRef, patch);
  } else {
    await setDoc(
      recordRef,
      {
        createdAt: serverTimestamp(),
        ...patch,
      },
      { merge: true }
    );
  }

  return patch;
}

export default {
  ALLERGEN_TRAINING_COURSE_ID,
  ALLERGEN_TRAINING_TITLE,
  ALLERGEN_TRAINING_REQUIRED_MONTHS,
  ALLERGEN_TRAINING_GRACE_DAYS,
  ALLERGEN_TRAINING_PASS_MARK,

  normaliseStaffId,
  getAllergenTrainingRecordId,
  getAllergenTrainingRecordRef,
  buildInitialAllergenTrainingRecord,
  ensureAllergenTrainingRecordForStaff,
  loadAllergenTrainingRecordsForVenue,
  loadAllergenTrainingGateForRota,
  buildAllergenTrainingStatus,
  canStaffBeAddedToRota,
  completeAllergenTraining,
};